import { useEffect, useRef } from 'react';

import { doc, setDoc, deleteDoc, serverTimestamp, collection } from 'firebase/firestore';

import { useDraftHandlers } from '../useDraftHandlers/useDraftHandlers';
import { useActivePostDraftId } from '../useActivePostDraftId/useActivePostDraftId';
import { db, auth, refreshFirebaseToken } from 'utils/firebase';
import { IPostStatus } from '_interfaces/types/post.types';
import { IPostType } from '_interfaces/systemPost.types';

import { DraftActionType, UseDraftsArgs } from './types';
import { getMediaType, getPayload, isEmptyDraft } from './utils';

const mapUploadsToMedia = (uploads: UseDraftsArgs['data']['uploads']) =>
  (uploads || []).map((u) => ({
    url: u.url || u.id || '',
    name: u.name || '',
    type: getMediaType(u.name || ''),
    description: u.description || null,
  }));

export const useDrafts = ({
  data,
  content,
  editorState,
  form,
  type,
  shouldSkip,
  companyId,
  user,
  existingFirestoreDraftId,
}: UseDraftsArgs) => {
  const draftIdRef = useRef<number | undefined>(undefined);
  const firestoreDraftIdRef = useRef<string | null>(existingFirestoreDraftId ?? null);
  const publishedRef = useRef(false);
  const initialContentRef = useRef(content);

  const handlers = useDraftHandlers(type);
  const { deleteActivePostDraftId } = useActivePostDraftId();

  const contentRef = useRef(content);
  contentRef.current = content;
  const dataRef = useRef(data);
  dataRef.current = data;
  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;
  const formRef = useRef(form);
  formRef.current = form;
  const shouldSkipRef = useRef(shouldSkip);
  shouldSkipRef.current = shouldSkip;

  const syncDraftToFirestore = async (
    actionType: DraftActionType,
    currentContent: string,
    currentData: UseDraftsArgs['data'],
    payload: Record<string, unknown>
  ) => {
    if (type !== 'POST' || !companyId || !user) return;

    let firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      const refreshSuccess = await refreshFirebaseToken();
      if (!refreshSuccess) {
        console.error('[syncDraftToFirestore] Firebase auth refresh failed — draft will not be saved');
        return;
      }
      firebaseUser = auth.currentUser;
    }

    const projectId = currentData.projectId;
    const basePath = `companies/${companyId}/projects/${projectId}/activityFeed`;

    if (actionType === 'CREATE_POST') {
      const docRef = doc(collection(db, basePath));
      firestoreDraftIdRef.current = docRef.id;

      await setDoc(docRef, {
        id: docRef.id,
        projectId: projectId.toString(),
        companyId: Number(companyId),
        isDeleted: false,
        isEdited: false,
        replyCount: 0,
        message: currentContent,
        mentions: payload.mentions || [],
        hashtags: payload.hashtagMentions || [],
        media: mapUploadsToMedia(currentData.uploads),
        likes: [],
        tags: [],
        status: IPostStatus.DRAFT,
        postType: IPostType.USER_POST,
        createdBy: {
          userId: user.userId,
          displayName: user.displayName,
          avatarUrl: user.avatarUrl,
          role: user.role,
        },
        createdAt: serverTimestamp(),
        phaseIds: currentData.phaseIds || [],
        phaseItemIds: currentData.phaseItemIds || [],
      });
    } else if (actionType === 'UPDATE_POST' && firestoreDraftIdRef.current) {
      const docRef = doc(db, `${basePath}/${firestoreDraftIdRef.current}`);
      await setDoc(
        docRef,
        {
          message: currentContent,
          mentions: payload.mentions || [],
          hashtags: payload.hashtagMentions || [],
          media: mapUploadsToMedia(currentData.uploads),
          phaseIds: currentData.phaseIds || [],
          phaseItemIds: currentData.phaseItemIds || [],
        },
        { merge: true }
      );
    } else if (actionType === 'DELETE_POST' && firestoreDraftIdRef.current) {
      const docRef = doc(db, `${basePath}/${firestoreDraftIdRef.current}`);
      await deleteDoc(docRef);
      firestoreDraftIdRef.current = null;
    }
  };

  const getActionType = (id: number | undefined, isEmpty: boolean): DraftActionType => {
    if (isEmpty && id) return `DELETE_${type}`;
    if (id) return `UPDATE_${type}`;
    return `CREATE_${type}`;
  };

  const saveDraft = async (
    currentContent: string,
    currentData: UseDraftsArgs['data'],
    currentEditorState: UseDraftsArgs['editorState'],
    currentForm: UseDraftsArgs['form']
  ) => {
    try {
      const isEmpty = isEmptyDraft(currentContent, currentData.uploads);
      if (isEmpty && !draftIdRef.current) return;

      const actionType = getActionType(draftIdRef.current, isEmpty);

      const payload = getPayload(
        {
          editorState: currentEditorState,
          content: currentContent,
          data: currentData,
          form: currentForm,
          id: draftIdRef.current,
        },
        actionType
      );

      await syncDraftToFirestore(
        actionType,
        currentContent,
        currentData,
        payload as Record<string, unknown>
      );

      handlers[actionType](payload).unwrap().then((result) => {
        if (actionType === 'CREATE_POST' || actionType === 'CREATE_COMMENT') {
          draftIdRef.current = (result as { id: number }).id;
        } else if (actionType === 'DELETE_POST') {
          deleteActivePostDraftId();
          draftIdRef.current = undefined;
        } else if (actionType === 'DELETE_COMMENT') {
          draftIdRef.current = undefined;
        }
      }).catch(() => {
        // Backend draft save failed — will retry on next save cycle
      });
    } catch (error) {
      console.error('[saveDraft] Draft save failed:', error);
    }
  };

  useEffect(() => {
    return () => {
      if (shouldSkipRef.current || publishedRef.current) return;
      // Skip saving if completing a draft and nothing changed
      if (existingFirestoreDraftId && contentRef.current === initialContentRef.current) return;
      saveDraft(contentRef.current, dataRef.current, editorStateRef.current, formRef.current);
    };
  }, []);

  const onPublish = () => {
    if (shouldSkip) return;
    publishedRef.current = true;

    // Don't delete the Firestore doc when completing an existing draft —
    // the publish flow updates it to ACTIVE status via handleCreatePostForDraft
    if (existingFirestoreDraftId) return;

    if (firestoreDraftIdRef.current && companyId && data.projectId) {
      const basePath = `companies/${companyId}/projects/${data.projectId}/activityFeed`;
      const docRef = doc(db, `${basePath}/${firestoreDraftIdRef.current}`);
      deleteDoc(docRef).catch((error) => {
        console.error('[useDrafts] Failed to clean up draft after publish:', error);
      });
      firestoreDraftIdRef.current = null;
    }
  };

  return {
    onPublish,
    isDraftSaving: handlers.results.isLoading,
  };
};
